import React, { useState, useEffect } from "react";
import { X, Save, Package, Tag, Wrench } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import toast from "react-hot-toast";

interface ItemCatalogo {
  id: number;
  codigo_interno: string;
  nombre: string;
  descripcion: string;
  categoria: string;
  tipo: "CONSUMIBLE" | "SERVICIO";
  unidad_medida: string;
  precio_unitario: number;
  stock_minimo: number;
  especificaciones: string;
  activo: boolean;
}

interface ItemCatalogoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  item?: ItemCatalogo | null;
}

// Categorías sugeridas (el usuario puede escribir otras)
const CATEGORIAS_SUGERIDAS = [
  "FILTROS",
  "FLUIDOS",
  "LUBRICANTES",
  "FRENOS",
  "SUSPENSION",
  "MOTOR",
  "ELECTRICO",
  "NEUMATICOS",
  "CARROCERIA",
  "PARTES",
  "INSUMOS",
  "MANO_OBRA",
  "TRANSMISION",
  "OTROS",
];

const UNIDADES = [
  { id: "UND", label: "Unidad (UND)" },
  { id: "L", label: "Litros (L)" },
  { id: "GAL", label: "Galones (GAL)" },
  { id: "KG", label: "Kilogramos (KG)" },
  { id: "SERV", label: "Servicio (SERV)" },
  { id: "HORA", label: "Hora (HR)" },
];

const ItemCatalogoModal: React.FC<ItemCatalogoModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  item,
}) => {
  const [loading, setLoading] = useState(false);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [formData, setFormData] = useState<Partial<ItemCatalogo>>({
    codigo_interno: "",
    nombre: "",
    descripcion: "",
    categoria: "OTROS",
    tipo: "CONSUMIBLE",
    unidad_medida: "UND",
    precio_unitario: 0,
    stock_minimo: 0,
    especificaciones: "",
    activo: true,
  });

  useEffect(() => {
    if (item) {
      setFormData({
        ...item,
        precio_unitario: Number(item.precio_unitario) || 0,
        stock_minimo: Number(item.stock_minimo) || 0,
      });
      // Si la categoría no está en las sugeridas, activar modo personalizado
      if (!CATEGORIAS_SUGERIDAS.includes(item.categoria)) {
        setShowCustomCategory(true);
      }
    } else {
      setFormData({
        codigo_interno: "",
        nombre: "",
        descripcion: "",
        categoria: "OTROS",
        tipo: "CONSUMIBLE",
        unidad_medida: "UND",
        precio_unitario: 0,
        stock_minimo: 0,
        especificaciones: "",
        activo: true,
      });
      setShowCustomCategory(false);
    }
  }, [item]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (item) {
        await maintenanceService.updateItemCatalogo(item.id, formData);
        toast.success("Ítem actualizado correctamente");
      } else {
        await maintenanceService.createItemCatalogo(formData);
        toast.success("Ítem registrado correctamente");
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving item:", error);
      const message =
        error.response?.data?.message || "Error al guardar el ítem";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-slate-900 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-2xl">
              <Package className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-black text-white uppercase tracking-wider">
                {item ? "Editar Ítem" : "Registrar Nuevo Ítem"}
              </h2>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-0.5">
                {item
                  ? `Ref: ${item.codigo_interno}`
                  : "Catálogo Maestro de Autopartes"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 text-white/50 hover:text-white rounded-xl transition-all"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Grid de Formulario */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Código Interno */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Código Interno
              </label>
              <div className="relative group">
                <Tag
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors"
                  size={18}
                />
                <Input
                  required
                  placeholder="FLT-001..."
                  value={formData.codigo_interno}
                  onChange={(e) =>
                    setFormData({ ...formData, codigo_interno: e.target.value })
                  }
                  className="pl-10 h-12 bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-indigo-500/20 rounded-xl font-bold"
                />
              </div>
            </div>

            {/* Categoría */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Categoría{" "}
                {showCustomCategory && (
                  <span className="text-indigo-600">(Personalizada)</span>
                )}
              </label>
              {showCustomCategory ? (
                <div className="space-y-2">
                  <Input
                    required
                    placeholder="Escribe tu categoría..."
                    value={formData.categoria}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        categoria: e.target.value.toUpperCase(),
                      })
                    }
                    className="h-12 bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-indigo-500/20 rounded-xl font-bold uppercase"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomCategory(false);
                      setFormData({ ...formData, categoria: "OTROS" });
                    }}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-bold"
                  >
                    Usar categorías predefinidas
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <select
                    required
                    value={formData.categoria}
                    onChange={(e) =>
                      setFormData({ ...formData, categoria: e.target.value })
                    }
                    className="w-full h-12 px-4 bg-slate-50 border-none focus:ring-2 focus:ring-indigo-500/20 rounded-xl font-bold text-sm"
                  >
                    {CATEGORIAS_SUGERIDAS.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowCustomCategory(true)}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-bold"
                  >
                    + Crear categoría personalizada
                  </button>
                </div>
              )}
            </div>

            {/* Nombre */}
            <div className="md:col-span-2 space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Nombre del Ítem
              </label>
              <Input
                required
                placeholder="Filtro de Aire / Aceite de Motor..."
                value={formData.nombre}
                onChange={(e) =>
                  setFormData({ ...formData, nombre: e.target.value })
                }
                className="h-12 bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-indigo-500/20 rounded-xl font-bold"
              />
            </div>

            {/* Descripción */}
            <div className="md:col-span-2 space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Descripción Corta
              </label>
              <textarea
                placeholder="Especificaciones rápidas del ítem..."
                value={formData.descripcion || ""}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                className="w-full p-4 bg-slate-50 border-none focus:ring-2 focus:ring-indigo-500/20 rounded-xl font-bold text-sm min-h-[80px]"
              />
            </div>

            {/* Tipo */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Tipo de Ítem
              </label>
              <div className="flex p-1 bg-slate-100 rounded-xl gap-1">
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      tipo: "CONSUMIBLE",
                      unidad_medida: "UND",
                    })
                  }
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${formData.tipo === "CONSUMIBLE"
                      ? "bg-white text-indigo-600 shadow-sm"
                      : "text-slate-500 hover:bg-white/50"
                    }`}
                >
                  <Package size={14} />
                  Repuesto
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      tipo: "SERVICIO",
                      unidad_medida: "SERV",
                    })
                  }
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${formData.tipo === "SERVICIO"
                      ? "bg-white text-indigo-600 shadow-sm"
                      : "text-slate-500 hover:bg-white/50"
                    }`}
                >
                  <Wrench size={14} />
                  Servicio
                </button>
              </div>
            </div>

            {/* Unidad de Medida */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Unidad de Medida
              </label>
              <select
                required
                value={formData.unidad_medida}
                onChange={(e) =>
                  setFormData({ ...formData, unidad_medida: e.target.value })
                }
                className="w-full h-12 px-4 bg-slate-50 border-none focus:ring-2 focus:ring-indigo-500/20 rounded-xl font-bold text-sm"
              >
                {UNIDADES.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Precio Referencial */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Precio Referencial (S/)
              </label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.precio_unitario}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    precio_unitario: Number(e.target.value),
                  })
                }
                className="h-12 bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-indigo-500/20 rounded-xl font-bold"
              />
            </div>

            {/* Stock Mínimo */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Alerta de Stock Mínimo
              </label>
              <Input
                type="number"
                placeholder="0"
                value={formData.stock_minimo}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    stock_minimo: Number(e.target.value),
                  })
                }
                className="h-12 bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-indigo-500/20 rounded-xl font-bold"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="h-12 px-6 font-black text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="h-12 px-10 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-200 border-none font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  <span>Guardando...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Save size={18} />
                  <span>{item ? "Actualizar Ítem" : "Guardar Ítem"}</span>
                </div>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ItemCatalogoModal;
